import Email20 from '@carbon/icons-react/es/email/20';
import { findLast } from 'lodash';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { AccountAPI, EmailConfig, EmailConfigBase } from 'api/account';
import { Loading } from 'components/atoms/loading';
import { Page } from 'components/molecules/page';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { GET_EMAIL_CONFIG, useGetDKIMConfig } from 'queries';
import { AddDomain } from './add-domain';
import { DomainDetails } from './domain-details';

const PageContent = styled(Page.Content)`
  padding: var(--layout-03);

  > .ant-row {
    padding: var(--spacing-03);
  }
`;

export const EmailConfiguration = () => {
  const title = useTranslation('email_configuration');
  const errorTitle = useTranslation('failure');
  const errorMessage = useTranslation('error');
  const successTitle = useTranslation('success');
  const updateSuccess = useTranslation(
    'settings.email_configuration.update.success',
  );
  const addSuccess = useTranslation(
    'settings.email_configuration.added.success',
  );

  const [loading, setLoading] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { data, isLoading, isSuccess } = useGetDKIMConfig();

  const addDomain = (domain: string) => {
    setLoading(true);

    AccountAPI.addEmailConfig({
      emailDomain: domain,
      enabled: true,
    })
      .then(() => {
        queryClient.invalidateQueries(GET_EMAIL_CONFIG);
        toastService({
          toastContainer: {},
          title: successTitle,
          message: addSuccess,
        }).success();
      })
      .catch(() => {
        toastService({
          toastContainer: {},
          title: errorTitle,
          message: errorMessage,
        }).error();
      })
      .finally(() => setLoading(false));
  };

  const deleteDomain = (emailDomain: string) => {
    setLoading(true);

    return AccountAPI.updateEmailConfig({
      emailDomain,
      enabled: false,
    })
      .then(() => queryClient.invalidateQueries(GET_EMAIL_CONFIG))
      .catch(() => {
        toastService({
          toastContainer: {},
          title: errorTitle,
          message: errorMessage,
        }).error();
      })
      .finally(() => setLoading(false));
  };

  const verifyDomain = (emailDomain: string) => {
    setLoading(true);

    return AccountAPI.verifyEmailConfig({
      emailDomain,
    })
      .then(() => queryClient.invalidateQueries(GET_EMAIL_CONFIG))
      .catch(() => {
        toastService({
          toastContainer: {},
          title: errorTitle,
          message: errorMessage,
        }).error();
      })
      .finally(() => setLoading(false));
  };

  const updateDomain = (payload: EmailConfigBase) => {
    setLoading(true);

    return AccountAPI.updateEmailConfig(payload)
      .then(() => {
        queryClient.invalidateQueries(GET_EMAIL_CONFIG);
        toastService({
          toastContainer: {},
          title: successTitle,
          message: updateSuccess,
        }).success();
      })
      .catch(() => {
        toastService({
          toastContainer: {},
          title: errorTitle,
          message: errorMessage,
        }).error();
      })
      .finally(() => setLoading(false));
  };

  return (
    <Page>
      <Helmet>{title}</Helmet>
      <Page.Header
        $white
        title={<HeaderWithIconTitle icon={<Email20 />} title={title} />}
      />
      <PageContent>
        {isLoading ? (
          <Loading />
        ) : isSuccess && findLast(data, { enabled: true }) ? (
          <DomainDetails
            config={findLast(data, { enabled: true }) as EmailConfig}
            onDelete={deleteDomain}
            loading={loading}
            onVerify={verifyDomain}
            onUpdate={updateDomain}
          />
        ) : (
          <AddDomain onAdd={addDomain} loading={loading} />
        )}
      </PageContent>
    </Page>
  );
};
